import { stripPdfOwnWidgetAnnots } from 'src/components/viewer/helpers';
import { useMaybeDocumentViewer } from 'src/components/viewer/viewer-context';
import { Apollo } from 'src/gql/generated';
import { NotificationsActionType, useNotificationDispatch } from 'src/notifications';

export function useSubscribeToAnnotationChanges(
  fileVersionId: UUID
): ReturnType<typeof Apollo.useOnAnnotationsChangedSubscription> {
  const dispatch = useNotificationDispatch();
  const documentViewer = useMaybeDocumentViewer();

  return Apollo.useOnAnnotationsChangedSubscription({
    // No cache b/c we have to modify cache ourselves to ensure only remote changes are applied.
    fetchPolicy: 'no-cache',
    onData: async ({ client, data: subscriptionData }) => {
      const annotationsQuery = client.readQuery<
        Apollo.GetAnnotationsQuery,
        Apollo.GetAnnotationsQueryVariables
      >(
        {
          query: Apollo.GetAnnotationsDocument,
          variables: { fileVersionId },
        },
        true
      );
      const eventXFDFString = subscriptionData.data?.annotationsChanged.annotationsXFDF?.xfdf;
      if (!eventXFDFString || !annotationsQuery?.annotations || !documentViewer) return;
      const userDisplayName =
        subscriptionData.data?.annotationsChanged.user.displayName ?? 'a remote user';
      const annotationManager = documentViewer.getAnnotationManager();

      /**
       * Remote is no different from local cache, so DON'T apply changes.
       * This prevents snackbar from flying out if user is making changes in their own tab.
       */
      const isRemoteSameAsCache = annotationsQuery.annotations.xfdf === eventXFDFString;
      if (isRemoteSameAsCache) return;

      await annotationManager.importAnnotations(stripPdfOwnWidgetAnnots(eventXFDFString));
      client.writeQuery<
        Apollo.UpsertAnnotationsMutation,
        Apollo.UpsertAnnotationsMutationVariables
      >({
        data: {
          __typename: 'Mutation',
          upsertAnnotations: {
            __typename: 'AnnotationsXFDF',
            id: annotationsQuery.annotations.id,
            xfdf: eventXFDFString,
          },
        },
        query: Apollo.UpsertAnnotationsDocument,
        variables: { input: { props: { fileVersionId, xfdf: eventXFDFString } } },
      });
      dispatch({
        payload: {
          message: `Change from ${userDisplayName} applied to document.`,
          variant: 'info',
        },
        type: NotificationsActionType.PUSH_NOTIFICATION,
      });
    },
    shouldResubscribe: true,
    variables: { fileVersionId },
  });
}
